/**
 * One file, looked at properly — the sheet that opens when a picture is pressed.
 *
 * The house equivalent of legacy's `FileDataModal`
 * (`src/shared/components/src/ModalsDialogs/FileDataModal/index.tsx`), which is
 * the one viewer legacy opens from everywhere a file is shown: the whole file
 * contained rather than cropped, and a way out under it. Three screens here
 * had grown their own version of it — the attachments list, the account sheet
 * and the member's photograph — at three sizes with three footers, which is
 * three chances for the next one to be a fourth.
 *
 * What legacy does that this cannot: its footer offers "Open in a new tab",
 * and a file that is not an image is handed to `window.open` instead of being
 * previewed at all. Both rest on the file having a URL a browser will navigate
 * to. Here every file — a photograph, an ID scan, an attachment — is stored as
 * a `data:` URL in a column, and Chrome and Firefox both refuse top-level
 * navigation to one, so that button would be a control that does nothing.
 * `download` is the same act the browser will still perform: the file leaves
 * the page, named, and opens in whatever the machine opens it with.
 *
 * Files stored since the move to object storage arrive as signed `https` URLs
 * instead, which a browser *would* navigate to — but the sheet is the same
 * either way, so there is still one viewer rather than two.
 */

import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { useTranslation } from "@/store/locale";

/**
 * What this viewer can show inline; anything else is a download.
 *
 * Two shapes, because a file is stored two ways. What the backend writes now
 * is an object-store key, handed back as a signed URL whose path keeps the
 * original extension — so the extension is what says "picture", the way the
 * media type does in a `data:` URL. Rows written before that move still carry
 * the file itself and are matched by the first test.
 */
export const isImageData = (url: string | null | undefined) => {
  const value = url ?? "";
  if (/^data:image\//i.test(value)) return true;
  // The query string of a signed URL carries its own dots, so the extension is
  // read from the path alone.
  return (
    /^https?:/i.test(value) &&
    /\.(jpe?g|png|gif|webp|avif|bmp|svg)$/i.test(value.split("?")[0] ?? "")
  );
};

/** A PDF, read the same two ways a picture is. */
export const isPdfData = (url: string | null | undefined) => {
  const value = url ?? "";
  return (
    /^data:application\/pdf/i.test(value) ||
    (/^https?:/i.test(value) && /\.pdf$/i.test(value.split("?")[0] ?? ""))
  );
};

/** Whether this viewer can show the file at all, rather than only save it. */
export const canPreview = (url: string | null | undefined) =>
  isImageData(url) || isPdfData(url);

export function FilePreview({
  src,
  title,
  alt,
  size = "lg",
  onDownload,
  download,
  downloading,
  actions,
  onClose,
}: {
  /** The file: a signed object-store URL, or a data URL on an older row. */
  src: string;
  title: string;
  /**
   * What the picture is, for a reader who cannot see it. Defaults to the
   * heading, which is what the file is called here — the sheet is labelled by
   * that heading, but an image with no name of its own leaves a screen reader
   * with nothing at all where the content is.
   */
  alt?: string | undefined;
  size?: "md" | "lg" | "xl";
  /**
   * What Download does, where the caller can do it properly.
   *
   * A handler rather than a filename and an `href`, because the file is a
   * presigned S3 URL: a browser ignores `download` on a cross-origin link, so
   * the file arrives named after the key it is stored under, and the bucket
   * sends no CORS header, so the page cannot fetch and rename it either. The
   * one place that can put the right name on it is the API, and only the
   * caller knows which row this is.
   */
  onDownload?: (() => void) | undefined;
  /**
   * The older form: a filename for a plain `<a href download>`.
   *
   * Still here because the help desk's attachments have no download route
   * behind them yet, and a link is what they have always used. It works
   * properly only for a `data:` URL — see `onDownload`, which is what a caller
   * with a route should pass instead. Ignored when `onDownload` is given.
   */
  download?: string | undefined;
  /** Whether that handler is busy, so the button can say so and not be pressed twice. */
  downloading?: boolean | undefined;
  /** Anything else this file can be done to, beside the way out. */
  actions?: ReactNode;
  onClose: () => void;
}) {
  const t = useTranslation();
  return (
    <Modal
      open
      onClose={onClose}
      size={size}
      title={title}
      footer={
        <>
          {/* The way out on the left of everything that acts, as every sheet
              in the app has it. */}
          <Button variant="secondary" onClick={onClose}>
            {t("common.close")}
          </Button>
          {onDownload ? (
            <Button
              onClick={onDownload}
              loading={downloading}
              loadingLabel={t("att.downloading")}
            >
              {t("hist.download")}
            </Button>
          ) : null}
          {actions}
        </>
      }
    >
      {/* Contained rather than cropped: this is the one place the whole stored
          file should be visible, edge to edge. The ground behind it is what
          makes a picture with its own white background readable as a picture. */}
      {/* Centred on both axes, and by flex rather than by a one-cell grid: a
          grid centres the item in its track, and a lone auto track is only as
          wide as the picture — so a portrait photograph sat wherever its track
          happened to land instead of in the middle of the sheet. */}
      <div className="bg-surface-inset flex min-h-40 items-center justify-center rounded-xl p-4">
        {isPdfData(src) ? (
          // An iframe, not an `<img>`: the browser's own PDF viewer is already
          // there, it pages and searches, and embedding costs no CORS — only
          // *reading* the bytes does, which is why the download goes through
          // the API while this does not.
          <iframe src={src} title={title} className="h-[70dvh] w-full rounded-lg border-0" />
        ) : (
          <img
            src={src}
            alt={alt ?? title}
            className="mx-auto max-h-[70dvh] w-auto max-w-full rounded-lg object-contain"
          />
        )}
      </div>
    </Modal>
  );
}
